import { useMemo } from "react"
import { useTranslations } from "next-intl"
import { type WizardStep, type WizardSteps } from "@krakentech/blueprint-onboarding"
import { type OnboardingRouteNames } from "@/routes/onboarding"
import { type OnboardingState, useOnboardingToStep } from "../../wizard"
import {
    ONBOARDING_FIRST_STEPS,
    ONBOARDING_STEPPER_NOT_CLICKABLE_AT,
    ONBOARDING_STEPPER_STEPS,
    ONBOARDING_STEPS_WITHOUT_STEPPER,
} from "../constants/onboardingStepperSteps"

type UseOnboardingStepperProps = {
    onboardingState: OnboardingState
    stepName: OnboardingRouteNames
    journeySteps: WizardSteps<OnboardingRouteNames>
}

function isStepperStep(
    step: WizardStep<OnboardingRouteNames>
): step is WizardStep<(typeof ONBOARDING_STEPPER_STEPS)[number]> {
    return ONBOARDING_STEPPER_STEPS.includes(step.name)
}

export function useOnboardingStepper({
    onboardingState,
    stepName,
    journeySteps,
}: UseOnboardingStepperProps) {
    const t = useTranslations("useOnboardingStepper")

    const { toStep } = useOnboardingToStep(stepName)

    const stepperProps = useMemo(() => {
        const stepperSteps = journeySteps.filter(isStepperStep)

        // Total number of steps in the stepper.
        const totalSteps = stepperSteps.length

        // Find the index of the lowest step that has not been submitted.
        const firstUnsubmittedStepIndex = stepperSteps.findIndex(
            (step) => !onboardingState.stepHistory.includes(step.name)
        )

        // Find the index of the current step in the stepper.
        const activeStepIndex = journeySteps.findIndex((step) => step.name === stepName)
        const activeMainStep = journeySteps
            .slice(0, activeStepIndex + 1)
            .findLast((step) => ONBOARDING_STEPPER_STEPS.includes(step.name))
        const activeMainStepIndex =
            stepperSteps.findIndex((step) => step.name === activeMainStep?.name) + 1

        // Generate the configuration for each step in the stepper.
        const stepConfigs = stepperSteps.map((step, i) => {
            const title = t(step.name)

            return {
                title,
                "aria-label": title,
                /**
                 * Disable steps that have not been submitted yet.
                 * If all have been submitted then firstUnsubmittedStepIndex will be -1.
                 */
                disabled: firstUnsubmittedStepIndex !== -1 && i >= firstUnsubmittedStepIndex,
            }
        })

        const isClickable = !ONBOARDING_STEPPER_NOT_CLICKABLE_AT.includes(stepName)

        const onStepClicked = async (stepNumber: number) => {
            const targetStep = stepperSteps[stepNumber - 1]

            if (targetStep.name !== stepName) await toStep(targetStep.name, onboardingState)
        }

        return {
            activeStep: activeMainStepIndex,
            totalSteps,
            stepConfigs,
            onStepClicked: isClickable ? onStepClicked : undefined,
        }
    }, [journeySteps, onboardingState, stepName, t, toStep])

    const hasStepper = useMemo(() => {
        if (ONBOARDING_STEPS_WITHOUT_STEPPER.includes(stepName)) {
            return false
        }

        const submittedMainSteps = journeySteps.filter((step) =>
            onboardingState.submittedSteps.includes(step.name)
        )

        return !ONBOARDING_FIRST_STEPS.includes(stepName) || submittedMainSteps.length > 1
    }, [journeySteps, onboardingState.submittedSteps, stepName])

    return { stepperProps, hasStepper }
}

useOnboardingStepper.messages = ["useOnboardingStepper"] as const
